<template>
  <MediaViewerModal
    :title="title"
    :download-url="downloadUrl"
    :media="video"
    :loading="loading"
    :error="error"
    loading-message="Loading video..."
    default-icon="fa-solid fa-video"
    :can-go-previous="canGoPrevious"
    :can-go-next="canGoNext"
    :go-previous="goPrevious"
    :go-next="goNext"
    @hide-modal="emit('hide-modal')"
    @retry="retryLoad"
    @download="onDownload"
  >
    <template #default="{ hiddenClass }">
      <div v-if="requiresProcessing" class="processing-message">
        <i class="fa-solid fa-gear fa-spin processing-icon"></i>
        <p class="processing-text">
          This video requires processing before it can be played. Please wait
          for the processing to be completed.
        </p>
      </div>
      <video
        v-else
        ref="videoPlayerRef"
        class="media-viewer-element video-element"
        controls
        playsinline
        :class="hiddenClass"
        @loadeddata="onVideoLoaded"
        @error="onVideoError"
      >
        Your browser does not support the video tag.
      </video>
    </template>
    <template #footer-end>
      <button
        v-if="hasMetadata"
        class="video-info-btn"
        :class="{ 'video-info-btn--active': infoVisible }"
        aria-label="Video info"
        @click="infoVisible = !infoVisible"
        @mouseenter="infoVisible = true"
        @mouseleave="infoVisible = false"
      >
        <i class="fa-solid fa-circle-info"></i>
        <div v-show="infoVisible" class="video-info-tooltip">
          <div
            v-for="item in metadataItems"
            :key="item.label"
            class="video-info-row"
          >
            <span class="video-info-label">{{ item.label }}</span>
            <span>{{ item.value }}</span>
          </div>
        </div>
      </button>
    </template>
  </MediaViewerModal>
</template>

<script setup lang="ts">
import Hls from "hls.js";
import {
  computed,
  nextTick,
  onBeforeUnmount,
  onMounted,
  ref,
  watch
} from "vue";
import { trackEvent } from "@/lib/pendo";

import MediaViewerModal from "@/components/MediaViewerModal.vue";
import { Video } from "@/typings/video";

// Map file extensions to MIME types for canPlayType check
const EXTENSION_TO_MIME: Record<string, string> = {
  ".mp4": "video/mp4",
  ".m4v": "video/mp4",
  ".webm": "video/webm",
  ".mov": "video/quicktime",
  ".avi": "video/x-msvideo",
  ".m3u8": "application/vnd.apple.mpegurl"
};

// Check if browser can play a given MIME type
function canBrowserPlay(mimeType: string): boolean {
  const video = document.createElement("video");
  const result = video.canPlayType(mimeType);
  // "probably" or "maybe" means it can play, "" means it cannot
  return result !== "";
}

const props = withDefaults(
  defineProps<{
    video: Video;
    canGoPrevious?: boolean;
    canGoNext?: boolean;
    goPrevious?: () => void;
    goNext?: () => void;
  }>(),
  {
    canGoPrevious: false,
    canGoNext: false,
    goPrevious: undefined,
    goNext: undefined
  }
);

const emit = defineEmits<{
  "hide-modal": [];
}>();

const videoPlayerRef = ref<HTMLVideoElement | null>(null);
const loading = ref(true);
const error = ref<string | null>(null);
const infoVisible = ref(false);
let hls: Hls | null = null;

const title = computed(() => props.video.name || "Video Player");

const hasMetadata = computed(() => {
  const m = props.video.metadata;
  return m && Object.keys(m).length > 0;
});

function formatDuration(seconds: number): string {
  const h = Math.floor(seconds / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  const s = Math.floor(seconds % 60);
  if (h > 0) return `${h}h ${m}m ${s}s`;
  if (m > 0) return `${m}m ${s}s`;
  return `${s}s`;
}

function formatBitrate(bps: number): string {
  if (bps >= 1_000_000) return `${(bps / 1_000_000).toFixed(1)} Mbps`;
  return `${Math.round(bps / 1000)} kbps`;
}

function formatFileSize(bytes: number): string {
  if (bytes >= 1_000_000_000) return `${(bytes / 1_000_000_000).toFixed(1)} GB`;
  if (bytes >= 1_000_000) return `${(bytes / 1_000_000).toFixed(1)} MB`;
  return `${Math.round(bytes / 1000)} KB`;
}

const metadataItems = computed(() => {
  const m = props.video.metadata;
  if (!m) return [];

  const items: { label: string; value: string }[] = [];
  if (m.width && m.height)
    items.push({ label: "Resolution", value: `${m.width}×${m.height}` });
  if (m.duration)
    items.push({ label: "Duration", value: formatDuration(m.duration) });
  if (m.codec) items.push({ label: "Codec", value: m.codec });
  if (m.bit_rate)
    items.push({ label: "Bitrate", value: formatBitrate(m.bit_rate) });
  if (m.file_size)
    items.push({ label: "File size", value: formatFileSize(m.file_size) });
  return items;
});

const downloadUrl = computed(() => {
  return props.video.download_url || props.video.file?.url || "";
});

const videoUrl = computed(() => {
  // Prefer HLS playlist URL if available, otherwise use original file for native formats
  const playlist = props.video.hls_playlist;
  if (!playlist) return props.video.file?.url;

  // Append signed params to the playlist URL so CloudFront can validate
  // the initial request. Segment URLs are handled by Lambda@Edge.
  const cookies = playlist.cookies;
  if (!cookies) return playlist.url;

  const params = new URLSearchParams({
    Policy: cookies["CloudFront-Policy"],
    Signature: cookies["CloudFront-Signature"],
    "Key-Pair-Id": cookies["CloudFront-Key-Pair-Id"]
  }).toString();
  const separator = playlist.url.includes("?") ? "&" : "?";
  return `${playlist.url}${separator}${params}`;
});

const isHls = computed(() => {
  const url = videoUrl.value || "";
  return url.includes(".m3u8") || !!props.video.hls_playlist;
});

const fileExtension = computed(() => {
  // Use videoUrl so HLS takes priority when available
  const url = videoUrl.value || "";
  const lastDot = url.lastIndexOf(".");
  if (lastDot === -1) return "";
  // Extract extension, handling query strings (e.g., ".m3u8?token=...")
  const afterDot = url.substring(lastDot).toLowerCase();
  const queryIndex = afterDot.indexOf("?");
  return queryIndex !== -1 ? afterDot.substring(0, queryIndex) : afterDot;
});

const isNativelyPlayable = computed(() => {
  const mimeType = EXTENSION_TO_MIME[fileExtension.value];
  if (!mimeType) return false;
  return canBrowserPlay(mimeType);
});

const requiresProcessing = computed(() => {
  // Video requires processing if:
  // - There IS a file URL (something to process)
  // - It's not natively playable
  // - HLS isn't available yet
  const hasFileUrl = !!props.video.file?.url;
  return hasFileUrl && !isNativelyPlayable.value && !props.video.hls_playlist;
});

function initPlayer() {
  loading.value = true;
  error.value = null;

  // Don't attempt playback if video requires processing
  if (requiresProcessing.value) {
    loading.value = false;
    return;
  }

  const video = videoPlayerRef.value;
  if (!video || !videoUrl.value) {
    error.value = "No video URL available";
    loading.value = false;
    return;
  }

  trackEvent("video_played", {
    video_id: props.video.id,
    job_id: props.video.job_id,
    has_hls: isHls.value,
    has_metadata: hasMetadata.value,
    file_extension: fileExtension.value
  });

  if (isHls.value) {
    initHlsPlayer(video);
  } else {
    initNativePlayer(video);
  }
}

function initHlsPlayer(video: HTMLVideoElement) {
  if (Hls.isSupported()) {
    hls = new Hls({ enableWorker: true, lowLatencyMode: false });

    hls.loadSource(videoUrl.value!);
    hls.attachMedia(video);

    hls.on(Hls.Events.MANIFEST_PARSED, () => {
      loading.value = false;
      video.play().catch(() => {
        // Autoplay blocked, user will need to click play
      });
    });

    hls.on(Hls.Events.ERROR, (_event, data) => {
      if (data.fatal) {
        loading.value = false;
        switch (data.type) {
          case Hls.ErrorTypes.NETWORK_ERROR:
            error.value = "Network error loading video. Please try again.";
            destroyPlayer();
            break;
          case Hls.ErrorTypes.MEDIA_ERROR:
            hls?.recoverMediaError();
            break;
          default:
            error.value = "Error loading video. Please try again.";
            destroyPlayer();
            break;
        }
      }
    });
  } else if (video.canPlayType("application/vnd.apple.mpegurl")) {
    // Native HLS support (Safari)
    video.src = videoUrl.value!;
  } else {
    // Fall back to MP4 if HLS not supported
    if (props.video.file?.url) {
      video.src = props.video.file.url;
    } else {
      error.value = "HLS playback is not supported in this browser.";
      loading.value = false;
    }
  }
}

function initNativePlayer(video: HTMLVideoElement) {
  video.src = videoUrl.value!;
  video.load();
}

function onVideoLoaded() {
  loading.value = false;
}

function onVideoError() {
  if (!error.value) {
    error.value = "Error loading video. The file may be unavailable.";
  }
  loading.value = false;
}

function retryLoad() {
  destroyPlayer();
  nextTick(() => {
    initPlayer();
  });
}

function onDownload() {
  trackEvent("video_downloaded", {
    video_id: props.video.id,
    job_id: props.video.job_id
  });
}

function destroyPlayer() {
  if (hls) {
    hls.destroy();
    hls = null;
  }
  const video = videoPlayerRef.value;
  if (video) {
    video.pause();
    video.removeAttribute("src");
    video.load();
  }
}

// Reinitialize player when video changes
watch(
  () => props.video.id,
  () => {
    destroyPlayer();
    nextTick(() => {
      initPlayer();
    });
  }
);

onMounted(() => {
  initPlayer();
});

onBeforeUnmount(() => {
  destroyPlayer();
});

defineExpose({
  loading,
  error,
  infoVisible,
  videoUrl,
  downloadUrl,
  isHls,
  requiresProcessing,
  isNativelyPlayable,
  fileExtension
});
</script>

<style scoped>
.processing-message {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 2rem;
  text-align: center;
  color: #6b7280;
}

.processing-icon {
  font-size: 3rem;
  margin-bottom: 1rem;
  color: #9ca3af;
}

.processing-text {
  font-size: 1rem;
  line-height: 1.5;
  max-width: 300px;
  margin: 0;
}

.video-info-btn {
  position: relative;
  background: none;
  border: none;
  cursor: pointer;
  color: #9ca3af;
  font-size: 0.875rem;
  padding: 0;
  line-height: 1;
  transition: color 0.15s;
}

.video-info-btn:hover,
.video-info-btn--active {
  color: #6b7280;
}

.video-info-tooltip {
  position: absolute;
  bottom: calc(100% + 10px);
  right: 0;
  background: rgba(0, 0, 0, 0.85);
  border-radius: 6px;
  padding: 8px 12px;
  white-space: nowrap;
  font-size: 0.75rem;
  color: #e5e7eb;
  pointer-events: none;
}

.video-info-row {
  display: flex;
  justify-content: space-between;
  gap: 16px;
  line-height: 1.7;
}

.video-info-label {
  color: #9ca3af;
}
</style>
